import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp, Inbox, Handshake, Clock, Building2, Users, CheckCircle2, Plus,
  ArrowRight, ChevronRight, Check,
} from "lucide-react";
import { MatchScore, Fade } from "@/components/proptech";
import { api, track, timeAgo } from "@/lib/api";

const TYPE_META = {
  STRONG_MATCH: { icon: TrendingUp, tone: "bg-accent/15 text-accent", badgeTone: "text-slate-500" },
  BUYER_NO_INVENTORY: { icon: TrendingUp, tone: "bg-accent/15 text-accent", badgeTone: "text-slate-500" },
  NEW_INQUIRY: { icon: Inbox, tone: "bg-blue-50 text-blue-600", badgeTone: "text-blue-600" },
  COLLABORATION_ACTION: { icon: Handshake, tone: "bg-accent/15 text-accent", badgeTone: "text-accent" },
  LISTING_FRESHNESS: { icon: Clock, tone: "bg-amber-50 text-amber-600", badgeTone: "text-amber-600" },
  BUYER_NEED_FRESHNESS: { icon: Clock, tone: "bg-amber-50 text-amber-600", badgeTone: "text-amber-600" },
};

function timeAgoSafe(iso) {
  try { return timeAgo(iso); } catch { return ""; }
}

function OpportunityRow({ op, ai }) {
  const meta = TYPE_META[op.type] || TYPE_META.STRONG_MATCH;
  const Icon = meta.icon;
  const reasonText = (ai && ai.summary) ? ai.summary : op.reason;
  return (
    <Link
      to={op.next_action.destination}
      onClick={() => track("opportunity_cta_click", { opportunity_type: op.type, ref: op.key })}
      data-testid={`opportunity-${op.key}`}
      className="group flex items-center gap-4 bg-card border border-border rounded-xl p-4 hover:shadow-md hover:border-slate-300 transition-all"
    >
      {op.score != null ? (
        <div className="shrink-0"><MatchScore score={op.score} size={52} /></div>
      ) : (
        <div className={`shrink-0 h-11 w-11 rounded-lg flex items-center justify-center ${meta.tone}`}>
          <Icon className="h-5 w-5" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-semibold uppercase tracking-wider ${meta.badgeTone}`}>{op.badge}</span>
          {op.band && <span className="text-[10px] font-semibold text-slate-400">· {op.band}</span>}
          {op.time && <span className="text-[11px] text-slate-400">· {timeAgoSafe(op.time)}</span>}
        </div>
        <div className="font-semibold text-slate-900 truncate mt-0.5">{op.title}</div>
        <div className="text-sm text-slate-500 truncate">{reasonText}</div>
        {op.criteria?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`why-${op.key}`}>
            {op.criteria.map((c) => (
              <span key={c.label} className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5">
                <Check className="h-3 w-3" /> {c.label}
              </span>
            ))}
          </div>
        )}
      </div>
      <span className="shrink-0 hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-accent group-hover:gap-2 transition-all">
        {op.next_action.action_label} <ArrowRight className="h-4 w-4" />
      </span>
      <ChevronRight className="h-5 w-5 text-slate-300 sm:hidden shrink-0" />
    </Link>
  );
}

function EmptyState({ icon: Icon, title, body, cta, to }) {
  return (
    <div className="bg-card border border-dashed border-border rounded-xl p-6 text-center">
      <div className="mx-auto h-11 w-11 rounded-full bg-secondary flex items-center justify-center text-slate-400 mb-3">
        <Icon className="h-5 w-5" />
      </div>
      <div className="font-semibold text-slate-900">{title}</div>
      <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">{body}</p>
      {cta && (
        <Link to={to} className="mt-4 inline-flex items-center gap-2 bg-primary text-primary-foreground text-sm font-semibold px-4 py-2 rounded-lg hover:bg-primary/90 active:scale-95 transition-all">
          <Plus className="h-4 w-4" /> {cta}
        </Link>
      )}
    </div>
  );
}

/* Requires Attention: the prioritized operational feed (matches, inquiries,
   collaboration actions, freshness). Same deterministic data + CTAs as before —
   just relocated from the Dashboard into the Opportunities workspace. */
export function OpportunityFeed() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [briefing, setBriefing] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/dashboard/opportunities")
      .then((r) => { if (alive) { setData(r.data); setLoaded(true); } })
      .catch(() => { if (alive) { setData({ opportunities: [], kpis: {}, summary: null }); setLoaded(true); } });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!loaded || !data || !(data.opportunities || []).length) return;
    let alive = true;
    api.get("/dashboard/briefing").then((r) => { if (alive) setBriefing(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [loaded, data]);

  const aiItems = useMemo(() => {
    const m = {};
    (briefing?.items || []).forEach((it) => { if (it.key) m[it.key] = it; });
    return m;
  }, [briefing]);

  const opportunities = data?.opportunities || [];
  const kpis = data?.kpis || {};
  const hasInventory = (kpis.active_properties || 0) > 0 || (kpis.active_buyer_needs || 0) > 0;

  return (
    <div data-testid="requires-attention-feed">
      {!loaded && <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-[86px] bg-secondary/60 rounded-xl animate-pulse" />)}</div>}

      {loaded && opportunities.length > 0 && (
        <div className="space-y-3">
          {opportunities.map((op, i) => <Fade key={op.key} delay={0.03 * i}><OpportunityRow op={op} ai={aiItems[op.key]} /></Fade>)}
        </div>
      )}

      {loaded && opportunities.length === 0 && !hasInventory && (
        <div className="grid sm:grid-cols-2 gap-4">
          <EmptyState icon={Building2} title="No active properties"
            body="Add a property so PropertyConnect can connect it with relevant Buyer Needs across the network."
            cta="Add Property" to="/have-property" />
          <EmptyState icon={Users} title="No active buyer needs"
            body="Have a client looking for a property? Post a Buyer Need to discover relevant inventory."
            cta="Post Buyer Need" to="/have-buyer" />
        </div>
      )}

      {loaded && opportunities.length === 0 && hasInventory && (
        <EmptyState icon={CheckCircle2} title="You're all caught up"
          body="New matches, inquiries and collaboration opportunities will appear here as they become available." />
      )}
    </div>
  );
}
